import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridColumnMenuFilterUIProps } from '@progress/kendo-react-grid';
import moment from 'moment/moment';
import { FilterUi, GenericEvent } from '@components-clean/core/grid/grid-custom-filter';
import {
    boolOperators,
    numericAndDateOperators,
    operatorList,
} from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.data';
import {
    DataType,
    FilterFormValues,
    FilterOperators,
} from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.interface';
import {
    LogicOperator,
    ValueLabel,
} from '@components-clean/core/study-data-filter/advanced-select/advanced-select.interface';
import { useTranslation } from '@hooks/index';

const groupOperators = [
    { label: 'AND', operator: LogicOperator.AND },
    { label: 'OR', operator: LogicOperator.OR },
];

export const DateFilter = (props: GridColumnMenuFilterUIProps) => {
    const { t } = useTranslation();

    const { firstFilterProps, secondFilterProps, onLogicChange, logicValue } = props;

    type FilterValue = {
        value: Date | null;
        logic: FilterOperators;
    };
    const isFilterValue = (obj: unknown): obj is FilterValue => {
        const typed = obj as FilterValue;
        return typed !== undefined && typed.value !== undefined && typed.logic in FilterOperators;
    };

    const processDate = (date: Date | null) => moment(date);
    const endOfDay = (date: Date | null) =>
        processDate(date).set({ hour: 23, minute: 59, second: 59, millisecond: 999 });

    const eqOperator = (negate: boolean) => (data: Date, filter: FilterValue) => {
        const utcFst = moment(data).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        const utcSnd = moment(filter.value).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        return negate !== utcFst.isSame(utcSnd); // Simple XOR
    };

    // Method to create a function to provide to Kendo to filter data, based on the FilterOperators provided
    // (from those that can be used for dates). If an unexpected operator is provided, filter includes no records
    const operator = (op: FilterOperators): ((data: Date, filter: FilterValue) => boolean) | string => {
        switch (op) {
            case FilterOperators.Equal:
                return eqOperator(false);
            case FilterOperators.NotEqual:
                return eqOperator(true);
            case FilterOperators.LessThanEqualTo:
                return (data: Date, filter: FilterValue) => {
                    if (filter.value) return data > filter.value || eqOperator(false)(data, filter);
                    return false;
                };
            case FilterOperators.LessThan:
                return (data: Date, filter: FilterValue) => {
                    if (filter.value) return data < filter.value;
                    return false;
                };
            case FilterOperators.GreaterThanEqualTo:
                return (data: Date, filter: FilterValue) => {
                    if (filter.value) return data < filter.value || eqOperator(false)(data, filter);
                    return false;
                };
            case FilterOperators.GreaterThan:
                return (data: Date, filter: FilterValue) => {
                    const filterValue = endOfDay(filter.value);
                    if (filter.value) return moment(data) > filterValue;
                    return false;
                };
            case FilterOperators.IsNull:
                return (data: Date) => {
                    return !data;
                };
            case FilterOperators.IsNotNull:
                return (data: Date) => {
                    return !!data;
                };
            // Shouldn't ever have an unexpected FilterOperator, if so then filter will include no records
            default:
                return () => false;
        }
    };

    const operators = operatorList
        .filter((x) => numericAndDateOperators.includes(x.value))
        .map((x) => ({
            value: x.value,
            label: t(x.label),
        }));

    const { control, getValues } = useForm<FilterFormValues>({
        defaultValues: {
            value1: isFilterValue(firstFilterProps.value) ? firstFilterProps.value.value : null,
            operator1: operators.find(
                (x) =>
                    x.value ===
                    (isFilterValue(firstFilterProps.value) ? firstFilterProps.value.logic : FilterOperators.Equal),
            ),
            value2: isFilterValue(secondFilterProps.value) ? secondFilterProps.value.value : null,
            operator2: operators.find(
                (x) =>
                    x.value ===
                    (isFilterValue(secondFilterProps.value) ? secondFilterProps.value.logic : FilterOperators.Equal),
            ),
            groupOperator: groupOperators.find(
                (x) => (x.operator === LogicOperator.AND ? 'and' : 'or') === logicValue?.operator,
            ),
        },
    });

    const onChange = (field: string) => (event: GenericEvent) => {
        const values = getValues();
        if (field === 'value1' || field === 'operator1') {
            if (!(values.value1 instanceof Date)) return;
            const value: FilterValue = { value: values.value1, logic: values.operator1.value };
            firstFilterProps.onChange({
                value,
                operator: operator(values.operator1.value),
                syntheticEvent: event.syntheticEvent,
            });
        }

        if (field === 'value2' || field === 'operator2') {
            if (!(values.value2 instanceof Date)) return;
            const value: FilterValue = { value: values.value2, logic: values.operator2.value };
            secondFilterProps.onChange({
                value,
                operator: operator(values.operator2.value),
                syntheticEvent: event.syntheticEvent,
            });
        }

        if (field === 'groupOperator') {
            const groupEvent = {
                ...event,
                value: {
                    text: values.groupOperator.label,
                    operator: values.groupOperator.value === LogicOperator.AND ? 'and' : 'or',
                },
                target: {
                    value: {
                        text: values.groupOperator.label,
                        operator: values.groupOperator.value === LogicOperator.AND ? 'and' : 'or',
                    },
                },
            } as ComboBoxChangeEvent;
            onLogicChange(groupEvent);
        }
    };

    const rend = FilterUi(control, operators, (field) => onChange(field), false, false, DataType.Date, true);

    return <form>{rend}</form>;
};

export const BooleanFilter = (props: GridColumnMenuFilterUIProps) => {
    const { t } = useTranslation();
    const { firstFilterProps, secondFilterProps, onLogicChange, logicValue } = props;

    const [value1Disabled, setValue1Disabled] = useState<boolean>(false);
    const [value2Disabled, setValue2Disabled] = useState<boolean>(false);

    type FilterValue = {
        value: ValueLabel<boolean | null> | null;
        logic: FilterOperators;
    };
    const isFilterValue = (obj: unknown): obj is FilterValue => {
        const typed = obj as FilterValue;
        return typed !== undefined && typed.value !== undefined && typed.logic in FilterOperators;
    };

    const operators = operatorList
        .filter((x) => boolOperators.includes(x.value))
        .map((x) => ({
            value: x.value,
            label: t(x.label),
        }));
    const { control, getValues } = useForm<FilterFormValues>({
        defaultValues: {
            value1: isFilterValue(firstFilterProps.value) ? firstFilterProps.value.value : null,
            operator1: operators.find(
                (x) =>
                    x.value ===
                    (isFilterValue(firstFilterProps.value) ? firstFilterProps.value.logic : FilterOperators.Equal),
            ),
            value2: isFilterValue(secondFilterProps.value) ? secondFilterProps.value.value : null,
            operator2: operators.find(
                (x) =>
                    x.value ===
                    (isFilterValue(secondFilterProps.value) ? secondFilterProps.value.logic : FilterOperators.Equal),
            ),
            groupOperator: groupOperators.find(
                (x) => (x.operator === LogicOperator.AND ? 'and' : 'or') === logicValue?.operator,
            ),
        },
    });

    // Method to create a function to provide to Kendo to filter data, based on the FilterOperators provided
    // (from those that can be used for booleans). If an unexpected operator is provided, filter includes no records
    const operator = (op: FilterOperators): ((data: boolean | null, filter: FilterValue) => boolean) | string => {
        switch (op) {
            case FilterOperators.Equal:
                return (data, filter) => data === filter.value?.value;
            case FilterOperators.NotEqual:
                return (data, filter) => data !== filter.value?.value;
            case FilterOperators.IsNull:
                return (data) => data === null;
            case FilterOperators.IsNotNull:
                return (data) => data !== null;
            default:
                // If we somehow have another filter operator, this is unhandled so return no rows
                return () => false;
        }
    };

    // Method to handle FilterUI form changes
    const onChange = (field: string) => (event: GenericEvent) => {
        const values = getValues();
        let disableValue1 = false;
        let disableValue2 = false;

        if (
            (field === 'operator1' || field === 'operator2') &&
            (values.operator1.value === FilterOperators.IsNull || values.operator1.value === FilterOperators.IsNotNull)
        ) {
            disableValue1 = true;
        }

        if (
            (field === 'operator1' || field === 'operator2') &&
            (values.operator2.value === FilterOperators.IsNull || values.operator2.value === FilterOperators.IsNotNull)
        ) {
            disableValue2 = true;
        }

        setValue1Disabled(disableValue1);
        setValue2Disabled(disableValue2);

        if (field === 'value1' || field === 'operator1') {
            const value: FilterValue = {
                value: values.value1 as ValueLabel<boolean | null>,
                logic: values.operator1.value,
            };
            firstFilterProps.onChange({
                value,
                operator: operator(values.operator1.value),
                syntheticEvent: event.syntheticEvent,
            });
        }

        if (field === 'value2' || field === 'operator2') {
            const value: FilterValue = {
                value: values.value2 as ValueLabel<boolean | null>,
                logic: values.operator2.value,
            };
            secondFilterProps.onChange({
                value,
                operator: operator(values.operator2.value),
                syntheticEvent: event.syntheticEvent,
            });
        }

        if (field === 'groupOperator') {
            const groupEvent = {
                ...event,
                value: {
                    text: values.groupOperator.label,
                    operator: values.groupOperator.value === LogicOperator.AND ? 'and' : 'or',
                },
                target: {
                    value: {
                        text: values.groupOperator.label,
                        operator: values.groupOperator.value === LogicOperator.AND ? 'and' : 'or',
                    },
                },
            } as ComboBoxChangeEvent;
            onLogicChange(groupEvent);
        }
    };

    const rend = FilterUi(
        control,
        operators,
        (field) => onChange(field),
        value1Disabled,
        value2Disabled,
        DataType.Bool,
        true,
    );

    return <form>{rend}</form>;
};
